import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useQueryClient } from '@tanstack/vue-query';
import { api } from '@/utils/api';
import { CurrentUserResponseDto } from '@/model/generated';
import { DEFAULT_LOCALE, useI18n } from '@/composables/useI18n';
import { LanguageLocale } from '@/model/language-locale';

const isLoggedIn = ref(false);
const logoutLoading = ref(false);
const initiallyCheckedLogin = ref(false);
const user = ref<CurrentUserResponseDto | null>(null);
export const useAuth = () => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { switchLocale } = useI18n();
  const setUser = async () => {
    return api
      .get('/user')
      .then(({ data }) => {
        user.value = data;
      })
      .catch(() => {
        isLoggedIn.value = false;
        user.value = null;
      });
  };

  const setAsLoggedIn = async () => {
    await setUser();

    isLoggedIn.value = true;

    await switchLocale(
      (user.value?.locale || DEFAULT_LOCALE).toLowerCase() as LanguageLocale
    );
  };

  const checkIfAuth = async () => {
    try {
      await api.post('/refresh-token').then(() => setUser());

      isLoggedIn.value = true;
    } catch {
      isLoggedIn.value = false;
    }

    initiallyCheckedLogin.value = true;
  };

  const logout = async () => {
    logoutLoading.value = true;
    await api.post('/sign-out').finally(async () => {
      isLoggedIn.value = false;
      logoutLoading.value = false;
      await router.push(`/`);
      user.value = null;
      queryClient.clear();
    });
  };

  const isClient = () => {
    return !!user.value?.client;
  };

  const isEmergencyContact = () => {
    return !!user.value?.emergencyContact;
  };

  return {
    user,
    isLoggedIn,
    logout,
    setAsLoggedIn,
    isClient,
    isEmergencyContact,
    checkIfAuth,
    logoutLoading,
    initiallyCheckedLogin,
  };
};
